import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, MessageCircle as MessageIcon, Share2, User as UserIcon, 
  ArrowLeft, Play, Volume2, Image as ImageIcon, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import ImageLightbox from '../components/ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Plumbob component
const Plumbob = ({ color = 'purple' }) => (
  <motion.svg
    viewBox="0 0 24 24"
    className="w-6 h-6"
    animate={{ rotateY: [0, 180, 360] }}
    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
  >
    <defs>
      <linearGradient id="plumbob-feed" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3A5C8F" />
        <stop offset="100%" stopColor="#6366f1" />
      </linearGradient>
    </defs>
    <polygon points="12,2 22,12 12,22 2,12" fill="url(#plumbob-feed)" stroke="#2B4C7E" strokeWidth="1" />
  </motion.svg>
);

export default function Feed() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [whispers, setWhispers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    loadFeed();
  }, []);

  const loadFeed = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [feedRes, userRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/whispers/feed`, { headers }),
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers })
      ]);

      setWhispers(feedRes.data);
      setUser(userRes.data);
    } catch (error) {
      console.error('Failed to load feed:', error);
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = (whisperId) => {
    const newLiked = new Set(likedPosts);
    if (newLiked.has(whisperId)) {
      newLiked.delete(whisperId);
    } else {
      newLiked.add(whisperId);
    }
    setLikedPosts(newLiked);
  };

  const handleShare = (whisper) => {
    if (navigator.share) {
      navigator.share({
        title: whisper.title,
        text: whisper.content,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(whisper.content);
      toast.success('Content copied to clipboard');
    }
  };

  const openImageLightbox = (imageUrl) => {
    setSelectedImage(imageUrl);
    setLightboxOpen(true);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="h-4 w-4" />;
      case 'video': return <Play className="h-4 w-4" />;
      case 'audio': return <Volume2 className="h-4 w-4" />;
      default: return <MessageIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] via-[#EBF0F5] to-[#EBF0F5]">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="flex justify-center mb-4">
            <Plumbob />
          </div>
          <p className="text-gray-600 font-medium">Loading whispers feed...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#F7F1E8] via-[#EBF0F5] to-[#EBF0F5]" data-testid="feed-page">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-[#B9C7D6]/20 rounded-full blur-3xl"
          animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-[#B9C7D6]/20 rounded-full blur-3xl"
          animate={{ x: [0, -30, 0], y: [0, -50, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/70 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="rounded-full">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <Plumbob />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#2B4C7E] bg-clip-text text-transparent">
              Whispers Feed
            </h1>
          </div>
          {user && (
            <Link to="/profile">
              {user.profile_picture || user.picture ? (
                <img src={user.profile_picture || user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-[#D7E0EA]" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-[#3A5C8F] to-[#D0B384] rounded-full flex items-center justify-center">
                  <UserIcon className="h-5 w-5 text-white" />
                </div>
              )}
            </Link>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-2xl mx-auto p-4 md:p-8">
        {whispers.length === 0 ? (
          <motion.div 
            className="text-center py-16"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-[#EBF0F5] to-[#F2E9D9] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Heart className="h-12 w-12 text-[#2B4C7E]" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent mb-3">
              No Whispers Yet
            </h3>
            <p className="text-gray-600 mb-6">Be the first to share a whisper with your loved ones</p>
            <Link to="/whispers">
              <Button className="bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white rounded-full shadow-lg">
                <Sparkles className="h-5 w-5 mr-2" />
                Create a Whisper
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6">
            <AnimatePresence>
              {whispers.map((whisper, index) => (
                <motion.div
                  key={whisper.whisper_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="rounded-3xl border-2 border-[#EBF0F5] shadow-lg hover:shadow-xl transition-all overflow-hidden bg-white/90 backdrop-blur-sm">
                    {/* Author Header */}
                    <div className="p-4 flex items-center gap-3">
                      {whisper.author?.profile_picture || whisper.author?.picture ? (
                        <img 
                          src={whisper.author.profile_picture || whisper.author.picture}
                          alt={whisper.author.name}
                          className="w-12 h-12 rounded-full object-cover ring-2 ring-[#EBF0F5]"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-[#3A5C8F] to-[#D0B384] rounded-full flex items-center justify-center">
                          <UserIcon className="h-6 w-6 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-bold text-gray-800">{whisper.author?.name}</p>
                        <p className="text-sm text-gray-500">{formatTimeAgo(whisper.created_at)}</p>
                      </div>
                      {whisper.type !== 'text' && (
                        <span className="px-3 py-1 bg-gradient-to-r from-[#EBF0F5] to-[#F2E9D9] text-[#1E3A63] rounded-full text-xs font-medium flex items-center gap-1">
                          {getMediaIcon(whisper.type)}
                          {whisper.type}
                        </span>
                      )}
                    </div>

                    {/* Media Content - Show first if exists */}
                    {whisper.media_url && (
                      <div className="relative">
                        {whisper.type === 'image' && (
                          <motion.img 
                            src={whisper.media_url} 
                            alt="Whisper media"
                            onClick={() => openImageLightbox(whisper.media_url)}
                            className="w-full max-h-[500px] object-cover cursor-pointer"
                            whileHover={{ scale: 1.01 }}
                            transition={{ duration: 0.2 }}
                          />
                        )}
                        {whisper.type === 'video' && (
                          <video 
                            src={whisper.media_url}
                            controls
                            className="w-full max-h-[500px] object-contain bg-black"
                            poster={whisper.thumbnail_url}
                          />
                        )}
                        {whisper.type === 'audio' && (
                          <div className="p-6 bg-gradient-to-r from-[#F7F1E8] to-[#F7F1E8]">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 bg-gradient-to-br from-[#D0B384] to-[#C8A97E] rounded-2xl flex items-center justify-center shadow-lg">
                                <Volume2 className="h-8 w-8 text-white" />
                              </div>
                              <audio 
                                src={whisper.media_url}
                                controls
                                className="flex-1"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Text Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{whisper.title}</h3>
                      {whisper.recipient_name && (
                        <p className="text-sm text-[#2B4C7E] mb-3 flex items-center gap-1">
                          <Heart className="h-4 w-4" />
                          For: {whisper.recipient_name}
                        </p>
                      )}
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{whisper.content}</p>
                      
                      {whisper.frequency && (
                        <div className="mt-4 px-3 py-2 bg-[#EBF0F5] rounded-xl inline-block">
                          <span className="text-sm text-[#1E3A63] font-medium capitalize">
                            🔄 {whisper.frequency} reminder
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="px-6 pb-4 flex items-center gap-2 border-t border-[#EBF0F5] pt-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLike(whisper.whisper_id)}
                        className={`rounded-full gap-2 flex-1 ${likedPosts.has(whisper.whisper_id) ? 'text-red-500 bg-red-50' : 'text-gray-600 hover:bg-[#EBF0F5]'}`}
                        data-testid={`like-${whisper.whisper_id}`}
                      >
                        <Heart 
                          className="h-5 w-5" 
                          fill={likedPosts.has(whisper.whisper_id) ? 'currentColor' : 'none'}
                        />
                        <span>{likedPosts.has(whisper.whisper_id) ? 'Liked' : 'Like'}</span>
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full gap-2 flex-1 text-gray-600 hover:bg-[#EBF0F5]"
                      >
                        <MessageIcon className="h-5 w-5" />
                        <span>Comment</span>
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShare(whisper)}
                        className="rounded-full gap-2 flex-1 text-gray-600 hover:bg-[#EBF0F5]"
                        data-testid={`share-${whisper.whisper_id}`}
                      >
                        <Share2 className="h-5 w-5" />
                        <span>Share</span>
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => {
          setLightboxOpen(false);
          setSelectedImage(null);
        }}
        imageSrc={selectedImage}
        imageAlt="Whisper media"
      />
    </div>
  );
}
